// SVG-иконки кейвордов — порт из Claude Design (Keyword Icons.html v1).
// Все 24×24, currentColor → перекрашиваются через CSS color.
//
// Используются:
//   - В туториал-экране «Способности карт» (56×56 на карточке)
//   - На юнитах на поле боя:
//       Провокация / Молчание — top-right corner (16×16)
//       Скрытность — overlay-серый + glyph-метка (12×12)
//       Заражён ×1/×2/×3 — bottom-strip, ряд по стакам (12×12)
//       Иммунитет — большой overlay на весь арт (полупрозрачный)
//       Подавление — top-left или внутри арта (gsec only)
//       Модификация — left column, по 1 значку на стак (10×14, синт only)
//
// Дефолтные цвета:
//   Taunt    — amber  #FFB13D
//   Stealth  — syn    #C93DFF
//   Silence  — cyan   #3DF0FF
//   Infected — liv-2  #3DFFA5
//   Immune   — lime   #9CFF3D
//   Suppress — gsec-1 #FF8A1F
//   Mod      — syn-1  #C93DFF

import type { SVGProps } from 'react';

type Props = SVGProps<SVGSVGElement>;

const COMMON_STROKE: Props = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'square',
  strokeLinejoin: 'miter'
};

// 01 · Провокация (TAUNT) — щит-стенка с восклицательным знаком.
export function IcoTaunt(props: Props) {
  return (
    <svg viewBox="0 0 24 24" {...COMMON_STROKE} {...props}>
      <path d="M12 2 L21 5 L21 11 L18.5 17.5 L12 22 L5.5 17.5 L3 11 L3 5 Z" />
      <path d="M12 7 L12 13.5" strokeWidth="2.2" />
      <path d="M12 15.5 L12 17" strokeWidth="2.2" />
    </svg>
  );
}

// 02 · Скрытность (STEALTH) — закрытый глаз: дуга века + ресницы.
export function IcoStealth(props: Props) {
  return (
    <svg viewBox="0 0 24 24" {...COMMON_STROKE} {...props}>
      <path d="M2 11 L7 15 L17 15 L22 11" />
      <path d="M4 18 L6 15.5" />
      <path d="M9 19 L9.5 16" />
      <path d="M12 19.5 L12 16.2" />
      <path d="M15 19 L14.5 16" />
      <path d="M20 18 L18 15.5" />
    </svg>
  );
}

// 03 · Молчание (SILENCE) — динамик + X-перечёркивание.
export function IcoSilence(props: Props) {
  return (
    <svg viewBox="0 0 24 24" {...COMMON_STROKE} {...props}>
      <path d="M3 9 L8 9 L13 4 L13 20 L8 15 L3 15 Z" fill="currentColor" fillOpacity="0.18" />
      <path d="M16.5 8.5 L22 14" />
      <path d="M22 8.5 L16.5 14" />
    </svg>
  );
}

// 04a · Заражён ×1 — верхняя лопасть трефоила.
export function IcoInfected1(props: Props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <polygon points="12,2 14.6,7 12,11 9.4,7" />
    </svg>
  );
}

// 04b · Заражён ×2 — верхняя + правая лопасти.
export function IcoInfected2(props: Props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <polygon points="12,2 14.6,7 12,11 9.4,7" />
      <polygon points="20.7,17 15.1,16.7 12.9,12.5 17.6,12.3" />
    </svg>
  );
}

// 04c · Заражён ×3 — полный трефоил + центральный ромб.
export function IcoInfected3(props: Props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <polygon points="12,2 14.6,7 12,11 9.4,7" />
      <polygon points="20.7,17 15.1,16.7 12.9,12.5 17.6,12.3" />
      <polygon points="3.3,17 6.4,12.3 11.1,12.5 8.9,16.7" />
      <polygon points="12,11 13.5,13 12,15 10.5,13" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

// 04 · Заражён универсальный (без указания стака) — для шпаргалки.
// Используем полный трефоил ×3.
export const IcoInfected = IcoInfected3;

// 05 · Иммунитет (IMMUNE-FIRST-HIT) — солидный щит с галочкой.
// Тёмный цвет галочки — через --kw-ink (по умолчанию #07070B).
export function IcoImmune(props: Props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12 2 L21 5 L21 11 L18.5 17.5 L12 22 L5.5 17.5 L3 11 L3 5 Z" />
      <path
        d="M9 11 L11 13 L15 9"
        fill="none"
        stroke="var(--kw-ink, #07070B)"
        strokeWidth="2.2"
        strokeLinecap="square"
        strokeLinejoin="miter"
      />
    </svg>
  );
}

// 06 · Подавление (SUPPRESSION) — стрелка вниз в октагон-контуре + земля.
export function IcoSuppression(props: Props) {
  return (
    <svg viewBox="0 0 24 24" {...COMMON_STROKE} {...props}>
      <path d="M6 3 L18 3 L21 6 L21 18 L18 21 L6 21 L3 18 L3 6 Z" />
      <path d="M12 6 L12 15" />
      <path d="M7.5 11.5 L12 16 L16.5 11.5" />
      <path d="M8 18 L16 18" />
    </svg>
  );
}

// 08 · Пробитие (PIERCE) — щит разорван по диагонали, стрела сквозь.
// Версия из Claude Design (Keyword Icons.html v1).
export function IcoPierce(props: Props) {
  return (
    <svg viewBox="0 0 24 24" {...COMMON_STROKE} {...props}>
      <path d="M11 4 L4 6.5 L4 11 L6 16 L8 18" />
      <path d="M13 4 L20 6.5 L20 11 L18 16 L13 19.5" />
      <line x1="3" y1="21" x2="21" y2="3" />
      <polyline points="14,3 21,3 21,10" />
      <polyline points="3,21 6.5,20 5,16.5" />
    </svg>
  );
}

// 07 · Модификация (Синтетикс) — hex-шестерёнка с 6 зубцами и квадратным портом.
export function IcoMod(props: Props) {
  return (
    <svg viewBox="0 0 24 24" {...COMMON_STROKE} {...props}>
      <polygon points="12,4.5 17.5,7.5 17.5,16.5 12,19.5 6.5,16.5 6.5,7.5" />
      <line x1="12" y1="4.5" x2="12" y2="1.5" />
      <line x1="17.5" y1="7.5" x2="20.1" y2="6" />
      <line x1="17.5" y1="16.5" x2="20.1" y2="18" />
      <line x1="12" y1="19.5" x2="12" y2="22.5" />
      <line x1="6.5" y1="16.5" x2="3.9" y2="18" />
      <line x1="6.5" y1="7.5" x2="3.9" y2="6" />
      <rect x="10" y="10" width="4" height="4" />
    </svg>
  );
}
